import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const QueryClientContext = createContext(null);
const hashKey = (queryKey) => JSON.stringify(Array.isArray(queryKey) ? queryKey : [queryKey]);
const keyMatches = (candidate, prefix) => {
  const left = Array.isArray(candidate) ? candidate : [candidate];
  const right = Array.isArray(prefix) ? prefix : [prefix];
  return right.every((value, index) => left[index] === value);
};

export class QueryClient {
  constructor({ defaultOptions = {} } = {}) {
    this.defaultOptions = defaultOptions;
    this.entries = new Map();
  }

  getEntry(queryKey) {
    const hash = hashKey(queryKey);
    if (!this.entries.has(hash)) {
      this.entries.set(hash, {
        queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        data: undefined,
        error: null,
        updatedAt: 0,
        isFetching: false,
        invalidated: true,
        promise: null,
        listeners: new Set(),
        queryFn: null,
        staleTime: 0,
      });
    }
    return this.entries.get(hash);
  }

  getSnapshot(queryKey) {
    const entry = this.getEntry(queryKey);
    return {
      data: entry.data,
      error: entry.error,
      isFetching: entry.isFetching,
      isPending: entry.data === undefined && !entry.error,
      updatedAt: entry.updatedAt,
    };
  }

  notify(entry) {
    entry.listeners.forEach((listener) => listener(this.getSnapshot(entry.queryKey)));
  }

  subscribe(queryKey, listener) {
    const entry = this.getEntry(queryKey);
    entry.listeners.add(listener);
    return () => entry.listeners.delete(listener);
  }

  async fetchQuery({ queryKey, queryFn, staleTime, force = false }) {
    const entry = this.getEntry(queryKey);
    const configuredStaleTime = Number.isFinite(Number(staleTime))
      ? Number(staleTime)
      : Number(this.defaultOptions?.queries?.staleTime || 0);

    entry.queryFn = queryFn;
    entry.staleTime = configuredStaleTime;

    const fresh = entry.data !== undefined
      && !entry.invalidated
      && Date.now() - entry.updatedAt < configuredStaleTime;
    if (!force && fresh) return entry.data;
    if (entry.promise) return entry.promise;

    entry.isFetching = true;
    entry.error = null;
    this.notify(entry);

    entry.promise = Promise.resolve()
      .then(queryFn)
      .then((data) => {
        entry.data = data;
        entry.updatedAt = Date.now();
        entry.invalidated = false;
        entry.error = null;
        return data;
      })
      .catch((error) => {
        entry.error = error;
        throw error;
      })
      .finally(() => {
        entry.isFetching = false;
        entry.promise = null;
        this.notify(entry);
      });

    return entry.promise;
  }

  setQueryData(queryKey, updater) {
    const entry = this.getEntry(queryKey);
    entry.data = typeof updater === 'function' ? updater(entry.data) : updater;
    entry.updatedAt = Date.now();
    entry.invalidated = false;
    entry.error = null;
    this.notify(entry);
    return entry.data;
  }

  async invalidateQueries({ queryKey = [] } = {}) {
    const tasks = [];
    this.entries.forEach((entry) => {
      if (!keyMatches(entry.queryKey, queryKey)) return;
      entry.invalidated = true;
      this.notify(entry);
      if (entry.listeners.size > 0 && entry.queryFn) {
        tasks.push(this.fetchQuery({
          queryKey: entry.queryKey,
          queryFn: entry.queryFn,
          staleTime: entry.staleTime,
          force: true,
        }).catch(() => undefined));
      }
    });
    await Promise.all(tasks);
  }

  removeQueries({ queryKey = [] } = {}) {
    [...this.entries.entries()].forEach(([hash, entry]) => {
      if (keyMatches(entry.queryKey, queryKey)) this.entries.delete(hash);
    });
  }

  clear() {
    this.entries.clear();
  }
}

export const QueryClientProvider = ({ client, children }) => (
  <QueryClientContext.Provider value={client}>{children}</QueryClientContext.Provider>
);

export const useQueryClient = () => {
  const client = useContext(QueryClientContext);
  if (!client) throw new Error('useQueryClient muss innerhalb des QueryClientProvider verwendet werden.');
  return client;
};

export const useQuery = ({ queryKey, queryFn, enabled = true, staleTime }) => {
  const client = useQueryClient();
  const keyHash = useMemo(() => hashKey(queryKey), [queryKey]);
  const stableKey = useMemo(() => JSON.parse(keyHash), [keyHash]);
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;
  const [snapshot, setSnapshot] = useState(() => client.getSnapshot(stableKey));

  useEffect(() => {
    setSnapshot(client.getSnapshot(stableKey));
    const unsubscribe = client.subscribe(stableKey, setSnapshot);
    if (enabled) {
      client.fetchQuery({
        queryKey: stableKey,
        queryFn: () => queryFnRef.current(),
        staleTime,
      }).catch(() => undefined);
    }
    return unsubscribe;
  }, [client, keyHash, enabled, staleTime]);

  return {
    ...snapshot,
    refetch: () => client.fetchQuery({
      queryKey: stableKey,
      queryFn: () => queryFnRef.current(),
      staleTime,
      force: true,
    }),
  };
};
